import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { LanguageSelector } from '../components/LanguageSelector';
import { AvatarContainer } from '../components/AvatarContainer';
import { AudioButton } from '../components/AudioButton';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { Progress } from '../components/ui/progress';
import axios from 'axios';
import { toast } from 'sonner';
import {
  LayoutDashboard,
  BookOpen,
  Trophy,
  User,
  LogOut,
  Clock,
  Star,
  CheckCircle,
  Lock,
  ChevronLeft,
  Play,
  Volume2,
  ChevronRight,
  BarChart3,
  Stethoscope,
  GraduationCap
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COURSE_CATALOG = [
  {
    id: 'menschen_a11',
    title: 'Menschen A1.1',
    subtitle: 'German for Beginners',
    subtitleAr: 'الألمانية للمبتدئين',
    color: '#3b82f6',
    bg: 'from-blue-500/10 to-blue-500/5',
    border: 'border-blue-500/20',
    link: '/menschen-a1',
    totalLessons: 12,
    modules: [
      { name: 'Modul 1', lessons: [
        { id: 'L1', label: 'Begrüßung & Befinden', icon: '👋' },
        { id: 'L2', label: 'Angaben zur Person & Berufe', icon: '💼' },
        { id: 'L3', label: 'Familie & Verwandte', icon: '👨‍👩‍👧‍👦' },
      ]},
      { name: 'Modul 2', lessons: [
        { id: 'L4', label: 'Möbel kaufen', icon: '🪑' },
        { id: 'L5', label: 'Im Haus & Räume', icon: '🏠' },
        { id: 'L6', label: 'Büro & Internet', icon: '💻' },
      ]},
      { name: 'Modul 3', lessons: [
        { id: 'L7', label: 'Hobbys & Freizeit', icon: '🎨' },
        { id: 'L8', label: 'Verabredungen & Termine', icon: '📅' },
        { id: 'L9', label: 'Essen & Trinken', icon: '🍽️' },
      ]},
      { name: 'Modul 4', lessons: [
        { id: 'L10', label: 'Reisen & Verkehr', icon: '🚆' },
        { id: 'L11', label: 'Tagesablauf', icon: '☀️' },
        { id: 'L12', label: 'Feste & Feiertage', icon: '🎉' },
      ]},
    ]
  },
  {
    id: 'menschen_a12',
    title: 'Menschen A1.2',
    subtitle: 'German for Beginners – Part 2',
    subtitleAr: 'الألمانية للمبتدئين – الجزء الثاني',
    color: '#6366f1',
    bg: 'from-indigo-500/10 to-indigo-500/5',
    border: 'border-indigo-500/20',
    link: '/menschen-a12',
    totalLessons: 12,
    modules: [
      { name: 'Modul 5', lessons: [
        { id: 'L13', label: 'Kleidung & Mode', icon: '👕' },
        { id: 'L14', label: 'Feste & Feiern', icon: '🎊' },
        { id: 'L15', label: 'Alltag & Routine', icon: '🏃' },
      ]},
      { name: 'Modul 6', lessons: [
        { id: 'L16', label: 'Schule & Ausbildung', icon: '🏫' },
        { id: 'L17', label: 'Wohnen & Umzug', icon: '🏢' },
        { id: 'L18', label: 'Gesundheit & Körper', icon: '🏥' },
      ]},
      { name: 'Modul 7', lessons: [
        { id: 'L19', label: 'Kontakte & Telefon', icon: '📱' },
        { id: 'L20', label: 'Aussehen & Beschreibung', icon: '👤' },
        { id: 'L21', label: 'Reisen & Urlaub', icon: '✈️' },
      ]},
      { name: 'Modul 8', lessons: [
        { id: 'L22', label: 'Arbeit & Büro', icon: '💼' },
        { id: 'L23', label: 'Medien & Kommunikation', icon: '📺' },
        { id: 'L24', label: 'Zukunft & Pläne', icon: '🌟' },
      ]},
    ]
  },
  {
    id: 'menschen_a21',
    title: 'Menschen A2.1',
    subtitle: 'German Pre-Intermediate',
    subtitleAr: 'الألمانية المتوسطة الأولى',
    color: '#0ea5e9',
    bg: 'from-sky-500/10 to-sky-500/5',
    border: 'border-sky-500/20',
    link: '/menschen-a21',
    totalLessons: 12,
    modules: [
      { name: 'Modul 1', lessons: [
        { id: 'L1', label: 'Familie', icon: '👨‍👩‍👧', grammar: 'Präteritum von sein/haben', grammarAr: 'الماضي البسيط', topics: ['Family & professions', 'Talking about the past'] },
        { id: 'L2', label: 'Umzug', icon: '📦', grammar: 'Wechselpräpositionen', grammarAr: 'حروف الجر المتبدلة', topics: ['Furniture & rooms', 'Giving directions'] },
        { id: 'L3', label: 'Feiern', icon: '🎉', grammar: 'Konjunktiv II mit würde', grammarAr: 'الشرطية', topics: ['Party planning', 'Expressing wishes'] },
      ]},
      { name: 'Modul 2', lessons: [
        { id: 'L4', label: 'Gefühle', icon: '😊', grammar: 'Reflexivpronomen + weil', grammarAr: 'الضمائر الانعكاسية', topics: ['Emotions & feelings', 'Giving reasons'] },
        { id: 'L5', label: 'Arbeit', icon: '💼', grammar: 'Nebensätze mit dass', grammarAr: 'جمل dass', topics: ['Home office vs office', 'Work-life balance'] },
        { id: 'L6', label: 'Mobilität', icon: '🚆', grammar: 'Komparativ & Superlativ', grammarAr: 'المقارنة والتفضيل', topics: ['Transportation', 'Comparing things'] },
      ]},
      { name: 'Modul 3', lessons: [
        { id: 'L7', label: 'Natur', icon: '🏕️', grammar: 'könnte/sollte + wenn', grammarAr: 'الاقتراحات', topics: ['Nature & camping', 'Weather'] },
        { id: 'L8', label: 'Freude', icon: '🎊', grammar: 'Verben + Präpositionen', grammarAr: 'أفعال + حروف جر', topics: ['Congratulations', 'Joy & sympathy'] },
        { id: 'L9', label: 'Termine', icon: '📅', grammar: 'Temporale Präp. + ob', grammarAr: 'حروف الجر الزمنية', topics: ['Doctor appointments', 'Cancelling/rescheduling'] },
      ]},
      { name: 'Modul 4', lessons: [
        { id: 'L10', label: 'Zufall', icon: '🎲', grammar: 'als vs. wenn', grammarAr: 'als مقابل wenn', topics: ['Telling stories', 'Past vs repeated events'] },
        { id: 'L11', label: 'Wohnen', icon: '🏠', grammar: 'Adjektivdeklination', grammarAr: 'تصريف الصفات', topics: ['Apartment hunting', 'WG life'] },
        { id: 'L12', label: 'Zukunft', icon: '🌟', grammar: 'Futur I: werden + Inf.', grammarAr: 'المستقبل', topics: ['Future plans', 'Goals & dreams'] },
      ]},
    ]
  },
  {
    id: 'menschen_a22',
    title: 'Menschen A2.2',
    subtitle: 'German Pre-Intermediate – Part 2',
    subtitleAr: 'الألمانية المتوسطة الأولى – الجزء الثاني',
    color: '#8b5cf6',
    bg: 'from-violet-500/10 to-violet-500/5',
    border: 'border-violet-500/20',
    link: '/menschen-a22',
    totalLessons: 12,
    modules: [
      { name: 'Modul 5', lessons: [
        { id: 'L13', label: 'Schule', icon: '🏫', grammar: 'Relativsätze', grammarAr: 'الجمل الموصولة', topics: ['School memories', 'Education system'] },
        { id: 'L14', label: 'Gesellschaft', icon: '👥', grammar: 'Passiv Präsens', grammarAr: 'المبني للمجهول', topics: ['Social changes', 'Digitalization'] },
        { id: 'L15', label: 'Events', icon: '🎭', grammar: 'obwohl / trotzdem / deshalb', grammarAr: 'رغم / مع ذلك / لذلك', topics: ['Events & culture', 'Recommendations'] },
      ]},
      { name: 'Modul 6', lessons: [
        { id: 'L16', label: 'Umwelt', icon: '🌿', grammar: 'um...zu / damit', grammarAr: 'لكي / حتى', topics: ['Environment', 'Waste separation'] },
        { id: 'L17', label: 'Beruf', icon: '💼', grammar: 'Infinitiv mit zu', grammarAr: 'المصدر مع zu', topics: ['Job applications', 'CV & interview'] },
        { id: 'L18', label: 'Gesundheit', icon: '🏥', grammar: 'sollte + wenn', grammarAr: 'النصائح الصحية', topics: ['Health advice', 'Doctor visits'] },
      ]},
      { name: 'Modul 7', lessons: [
        { id: 'L19', label: 'Erlebnisse', icon: '✨', grammar: 'bevor / nachdem / während', grammarAr: 'قبل / بعد / أثناء', topics: ['First experiences', 'Telling anecdotes'] },
        { id: 'L20', label: 'Regeln', icon: '📋', grammar: 'dürfen / müssen / sollen', grammarAr: 'القواعد والقوانين', topics: ['German rules', 'Cultural norms'] },
        { id: 'L21', label: 'Leben', icon: '🌍', grammar: 'Genitiv', grammarAr: 'المضاف إليه', topics: ['Life stories', 'Biographies'] },
      ]},
      { name: 'Modul 8', lessons: [
        { id: 'L22', label: 'Nachrichten', icon: '📰', grammar: 'Passiv Präteritum', grammarAr: 'المبني للمجهول ماضي', topics: ['News reports', 'Indirect speech'] },
        { id: 'L23', label: 'Arbeit', icon: '🏢', grammar: 'sowohl...als auch / weder...noch', grammarAr: 'كلاهما / لا هذا ولا ذاك', topics: ['Office life', 'Formal emails'] },
        { id: 'L24', label: 'Rückblick', icon: '🎓', grammar: 'A2 Zusammenfassung', grammarAr: 'ملخص A2', topics: ['Review all grammar', 'Motivation for B1'] },
      ]},
    ]
  },
  {
    id: 'pflege',
    title: 'Pflege Training',
    subtitle: 'Deutsch für Pflegekräfte',
    subtitleAr: 'الألمانية للممرضين',
    color: '#14b8a6',
    bg: 'from-teal-500/10 to-teal-500/5',
    border: 'border-teal-500/20',
    link: '/pflege-training',
    totalLessons: 16,
    modules: [
      { name: 'Grundlagen', lessons: [
        { id: 'M1', label: 'Begrüßung & Vorstellung', icon: '🤝' },
        { id: 'M2', label: 'Körperteile', icon: '🫁' },
        { id: 'M3', label: 'Alltag & Pflege', icon: '🛏️' },
        { id: 'M4', label: 'Zahlen & Datum', icon: '🔢' },
      ]},
      { name: 'Kommunikation', lessons: [
        { id: 'M5', label: 'Grammatik Basics', icon: '📝' },
        { id: 'M6', label: 'Pflege-Dialoge', icon: '💬' },
        { id: 'M7', label: 'Wortschatz erweitern', icon: '📚' },
        { id: 'M8', label: 'Notfälle', icon: '🚨' },
      ]},
      { name: 'Fachsprache', lessons: [
        { id: 'M9', label: 'Verben (Pflege)', icon: '🔄' },
        { id: 'M10', label: 'Adjektive (Medizin)', icon: '🌡️' },
        { id: 'M11', label: 'Familie & Angehörige', icon: '👨‍👩‍👧‍👦' },
        { id: 'M12', label: 'Dokumentation', icon: '📋' },
      ]},
      { name: 'Praxis', lessons: [
        { id: 'M13', label: 'Schichten & Übergabe', icon: '🕐' },
        { id: 'M14', label: 'Vitalzeichen', icon: '💓' },
        { id: 'M15', label: 'Mobilisation', icon: '🦽' },
        { id: 'M16', label: 'Quiz & Wiederholung', icon: '✅' },
      ]},
    ]
  },
];

export default function Lessons() {
  const { t, language } = useLanguage();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [lessons, setLessons] = useState([]);
  const [progress, setProgress] = useState({});
  const [courseProgress, setCourseProgress] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeLevel, setActiveLevel] = useState('courses');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [lessonsRes, progressRes, reportRes] = await Promise.all([
          axios.get(`${API}/lessons`, { withCredentials: true }),
          axios.get(`${API}/progress`, { withCredentials: true }),
          axios.get(`${API}/progress/report`, { withCredentials: true }).catch(() => ({ data: null }))
        ]);
        setLessons(lessonsRes.data);
        
        const progressMap = {};
        progressRes.data.lessons?.progress?.forEach(p => {
          progressMap[p.lesson_id] = p;
        });
        setProgress(progressMap);
        
        if (reportRes.data?.courses) {
          setCourseProgress(reportRes.data.courses);
        }
      } catch (error) {
        toast.error('Failed to load lessons');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const getLessonTitle = (lesson) => {
    if (language === 'de') return lesson.title_de;
    if (language === 'ar') return lesson.title_ar;
    return lesson.title_en;
  };

  const getLessonDescription = (lesson) => {
    if (language === 'de') return lesson.description_de;
    if (language === 'ar') return lesson.description_ar;
    return lesson.description_en;
  };

  const levelColors = {
    A1: 'border-teal-500 bg-teal-500/10',
    A2: 'border-blue-500 bg-blue-500/10',
    B1: 'border-purple-500 bg-purple-500/10',
    B2: 'border-amber-500 bg-amber-500/10'
  };

  const levelBadgeColors = {
    A1: 'bg-teal-500',
    A2: 'bg-blue-500',
    B1: 'bg-purple-500',
    B2: 'bg-amber-500'
  };

  const filteredLessons = lessons.filter(l => l.level === activeLevel);
  const groupedByModule = filteredLessons.reduce((acc, lesson) => {
    const module = lesson.module_number;
    if (!acc[module]) acc[module] = [];
    acc[module].push(lesson);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-background" data-testid="lessons-page">
      {/* Sidebar - Same as Dashboard */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-card border-r border-border hidden lg:block">
        <div className="p-6">
          <div className="flex items-center gap-2 mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_egns-deutsch/artifacts/wljgvqe2_copy_DF94F79F-AD21-40BD-8A6F-78D3B8D2676A.jpeg" 
              alt="EGNS Logo" 
              className="h-10 w-auto"
            />
          </div>

          <nav className="space-y-2">
            <Link
              to="/dashboard"
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-muted-foreground hover:bg-muted transition-colors"
            >
              <LayoutDashboard className="h-5 w-5" />
              {t('dashboard')}
            </Link>
            <Link
              to="/lessons"
              className="flex items-center gap-3 px-4 py-3 rounded-lg bg-primary/10 text-primary font-medium"
            >
              <BookOpen className="h-5 w-5" />
              {t('lessons')}
            </Link>
            <Link
              to="/achievements"
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-muted-foreground hover:bg-muted transition-colors"
            >
              <Trophy className="h-5 w-5" />
              {t('achievements')}
            </Link>
            <Link
              to="/profile"
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-muted-foreground hover:bg-muted transition-colors"
            >
              <User className="h-5 w-5" />
              {t('profile')}
            </Link>
          </nav>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-border">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 w-full rounded-lg text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
          >
            <LogOut className="h-5 w-5" />
            {t('logout')}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen pb-20 lg:pb-0">
        {/* Header */}
        <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-md border-b border-border">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-4">
              <Link to="/dashboard" className="lg:hidden">
                <Button variant="ghost" size="icon">
                  <ChevronLeft className="h-5 w-5" />
                </Button>
              </Link>
              <div>
                <h1 className="font-heading text-2xl font-bold">{t('lessons')}</h1>
                <p className="text-muted-foreground text-sm">Choose your level and start learning</p>
              </div>
            </div>
            <LanguageSelector />
          </div>
        </header>

        {/* Content */}
        <div className="p-6">
          {/* Level Tabs */}
          <Tabs value={activeLevel} onValueChange={setActiveLevel} className="mb-8">
            <TabsList className="grid w-full grid-cols-5 max-w-lg">
              <TabsTrigger value="courses" data-testid="tab-courses">All Courses</TabsTrigger>
              <TabsTrigger value="A1" data-testid="tab-a1">A1</TabsTrigger>
              <TabsTrigger value="A2" data-testid="tab-a2">A2</TabsTrigger>
              <TabsTrigger value="B1" data-testid="tab-b1">B1</TabsTrigger>
              <TabsTrigger value="B2" data-testid="tab-b2">B2</TabsTrigger>
            </TabsList>
          </Tabs>

          {loading ? (
            <div className="space-y-6">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={`skel-${i}`} className="h-32 rounded-xl" />
              ))}
            </div>
          ) : activeLevel === 'courses' ? (
            <div className="space-y-8" data-testid="course-catalog">
              {COURSE_CATALOG.map(course => {
                const cp = courseProgress[course.id] || {};
                const pct = cp.percentage || 0;
                const completed = cp.completed || 0;
                const completedLessons = cp.lessons || [];
                return (
                  <div key={course.id} data-testid={`catalog-${course.id}`}>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        {course.id === 'pflege' ? (
                          <Stethoscope className="h-6 w-6" style={{ color: course.color }} />
                        ) : (
                          <BookOpen className="h-6 w-6" style={{ color: course.color }} />
                        )}
                        <div>
                          <h2 className="font-heading text-lg font-semibold">{course.title}</h2>
                          <p className="text-xs text-muted-foreground">{course.subtitle} · <span style={{ color: course.color }}>{course.subtitleAr}</span></p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="text-sm font-mono font-bold" style={{ color: course.color }}>{pct}%</p>
                          <p className="text-xs text-muted-foreground">{completed}/{course.totalLessons}</p>
                        </div>
                        <Link to={course.link}>
                          <Button size="sm" style={{ background: course.color }} data-testid={`go-${course.id}`}>
                            <Play className="h-4 w-4 mr-1" /> Start
                          </Button>
                        </Link>
                      </div>
                    </div>
                    <Progress value={pct} className="h-1.5 mb-4" />
                    <div className="grid gap-4">
                      {course.modules.map((mod) => (
                        <Card key={`${course.id}-${mod.name}`} className={`bg-gradient-to-br ${course.bg} ${course.border}`}>
                          <CardContent className="pt-4 pb-3">
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3" style={{ color: course.color }}>{mod.name}</p>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                              {mod.lessons.map(lesson => {
                                const isDone = completedLessons.includes(lesson.id);
                                return (
                                  <Link to={course.link} key={lesson.id}>
                                    <div className={`flex items-start gap-2 p-2 rounded-lg border transition-colors hover:bg-accent/20 ${isDone ? 'opacity-60' : ''}`} data-testid={`lesson-${course.id}-${lesson.id}`}>
                                      <span className="text-lg mt-0.5">{lesson.icon}</span>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium truncate">{lesson.id}: {lesson.label}</p>
                                        {lesson.grammar && (
                                          <div className="mt-1">
                                            <p className="text-[11px] font-semibold" style={{ color: course.color }}>{lesson.grammar}</p>
                                            {lesson.grammarAr && <p className="text-[10px] text-muted-foreground" dir="rtl">{lesson.grammarAr}</p>}
                                            {lesson.topics && <p className="text-[10px] text-muted-foreground truncate">{lesson.topics.join(' · ')}</p>}
                                          </div>
                                        )}
                                      </div>
                                      {isDone && <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />}
                                    </div>
                                  </Link>
                                );
                              })}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="space-y-8">
              {/* Show A1 course catalog when A1 tab is selected */}
              {activeLevel === 'A1' && (
                <div className="space-y-6 mb-8" data-testid="a1-course-catalog">
                  {COURSE_CATALOG.filter(c => c.id.startsWith('menschen_a1')).map(course => {
                    const cp = courseProgress[course.id] || {};
                    const pct = cp.percentage || 0;
                    const completed = cp.completed || 0;
                    const completedLessons = cp.lessons || [];
                    return (
                      <div key={course.id} data-testid={`a1-catalog-${course.id}`}>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <BookOpen className="h-5 w-5" style={{ color: course.color }} />
                            <div>
                              <h2 className="font-heading text-lg font-semibold">{course.title}</h2>
                              <p className="text-xs text-muted-foreground">{course.subtitle}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-mono font-bold" style={{ color: course.color }}>{pct}%</span>
                            <Link to={course.link}>
                              <Button size="sm" style={{ background: course.color }} data-testid={`a1-go-${course.id}`}>
                                <Play className="h-4 w-4 mr-1" /> Start
                              </Button>
                            </Link>
                          </div>
                        </div>
                        <Progress value={pct} className="h-1.5 mb-3" />
                        <div className="grid gap-3">
                          {course.modules.map((mod) => (
                            <Card key={`${course.id}-${mod.name}`} className={`bg-gradient-to-br ${course.bg} ${course.border}`}>
                              <CardContent className="pt-4 pb-3">
                                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2" style={{ color: course.color }}>{mod.name}</p>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                  {mod.lessons.map(lesson => {
                                    const isDone = completedLessons.includes(lesson.id);
                                    return (
                                      <Link to={course.link} key={lesson.id}>
                                        <div className={`flex items-start gap-2 p-2 rounded-lg border transition-colors hover:bg-accent/20 ${isDone ? 'opacity-60' : ''}`}>
                                          <span className="text-lg mt-0.5">{lesson.icon}</span>
                                          <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium truncate">{lesson.id}: {lesson.label}</p>
                                            {lesson.grammar && (
                                              <div className="mt-1">
                                                <p className="text-[11px] font-semibold" style={{ color: course.color }}>{lesson.grammar}</p>
                                                {lesson.grammarAr && <p className="text-[10px] text-muted-foreground" dir="rtl">{lesson.grammarAr}</p>}
                                                {lesson.topics && <p className="text-[10px] text-muted-foreground truncate">{lesson.topics.join(' · ')}</p>}
                                              </div>
                                            )}
                                          </div>
                                          {isDone && <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />}
                                        </div>
                                      </Link>
                                    );
                                  })}
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                  {filteredLessons.length > 0 && (
                    <div className="border-t pt-6">
                      <h2 className="font-heading text-lg font-semibold mb-2 flex items-center gap-2">
                        <BookOpen className="h-5 w-5 text-teal-500" />
                        AI-Powered A1 Lessons
                      </h2>
                      <p className="text-sm text-muted-foreground mb-4">Interactive lessons with AI tutors</p>
                    </div>
                  )}
                </div>
              )}

              {/* Show A2 course catalog when A2 tab is selected */}
              {activeLevel === 'A2' && (
                <div className="space-y-6 mb-8" data-testid="a2-course-catalog">
                  {COURSE_CATALOG.filter(c => c.id.startsWith('menschen_a2')).map(course => {
                    const cp = courseProgress[course.id] || {};
                    const pct = cp.percentage || 0;
                    const completed = cp.completed || 0;
                    const completedLessons = cp.lessons || [];
                    return (
                      <div key={course.id} data-testid={`a2-catalog-${course.id}`}>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <GraduationCap className="h-5 w-5" style={{ color: course.color }} />
                            <div>
                              <h2 className="font-heading text-lg font-semibold">{course.title}</h2>
                              <p className="text-xs text-muted-foreground">{course.subtitle} · <span style={{ color: course.color }}>{course.subtitleAr}</span></p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-mono font-bold" style={{ color: course.color }}>{pct}%</span>
                            <Link to={course.link}>
                              <Button size="sm" style={{ background: course.color }} data-testid={`a2-go-${course.id}`}>
                                <Play className="h-4 w-4 mr-1" /> Start
                              </Button>
                            </Link>
                          </div>
                        </div>
                        <Progress value={pct} className="h-1.5 mb-3" />
                        <div className="grid gap-3">
                          {course.modules.map((mod) => (
                            <Card key={`${course.id}-${mod.name}`} className={`bg-gradient-to-br ${course.bg} ${course.border}`}>
                              <CardContent className="pt-4 pb-3">
                                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2" style={{ color: course.color }}>{mod.name}</p>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                  {mod.lessons.map(lesson => {
                                    const isDone = completedLessons.includes(lesson.id);
                                    return (
                                      <Link to={course.link} key={lesson.id}>
                                        <div className={`flex items-start gap-2 p-2.5 rounded-lg border transition-colors hover:bg-accent/20 ${isDone ? 'opacity-60' : ''}`} data-testid={`a2-lesson-${course.id}-${lesson.id}`}>
                                          <span className="text-lg mt-0.5">{lesson.icon}</span>
                                          <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium truncate">{lesson.id}: {lesson.label}</p>
                                            {lesson.grammar && (
                                              <div className="mt-1">
                                                <p className="text-[11px] font-semibold" style={{ color: course.color }}>{lesson.grammar}</p>
                                                {lesson.grammarAr && <p className="text-[10px] text-muted-foreground" dir="rtl">{lesson.grammarAr}</p>}
                                                {lesson.topics && <p className="text-[10px] text-muted-foreground truncate">{lesson.topics.join(' · ')}</p>}
                                              </div>
                                            )}
                                          </div>
                                          {isDone && <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />}
                                        </div>
                                      </Link>
                                    );
                                  })}
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {Object.entries(groupedByModule).map(([moduleNum, moduleLessons]) => (
                <div key={moduleNum}>
                  <h2 className="font-heading text-lg font-semibold mb-4">
                    Module {moduleNum}
                  </h2>
                  <div className="space-y-4">
                    {moduleLessons.map((lesson) => {
                      const lessonProgress = progress[lesson.lesson_id];
                      const isCompleted = lessonProgress?.completed;
                      
                      return (
                        <Card 
                          key={lesson.lesson_id}
                          className={`module-card overflow-hidden transition-all hover:shadow-lg ${
                            isCompleted ? 'opacity-75' : ''
                          }`}
                          data-testid={`lesson-${lesson.lesson_id}`}
                        >
                          <div className="flex items-center gap-4 p-4">
                            {/* Teacher Avatar */}
                            <AvatarContainer 
                              teacher={lesson.teacher} 
                              size="sm" 
                              showName={false}
                            />
                            
                            {/* Lesson Info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge className={levelBadgeColors[lesson.level]}>
                                  {lesson.level}
                                </Badge>
                                {isCompleted && (
                                  <Badge variant="outline" className="text-green-600 border-green-600">
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    {t('completed')}
                                  </Badge>
                                )}
                              </div>
                              <h3 className="font-semibold truncate flex items-center gap-2">
                                {getLessonTitle(lesson)}
                                <AudioButton text={lesson.title_de} voice={lesson.teacher === 'herr_amr' ? 'onyx' : 'nova'} />
                              </h3>
                              <p className="text-sm text-muted-foreground truncate">
                                {getLessonDescription(lesson)}
                              </p>
                              <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <Clock className="h-3 w-3" />
                                  {lesson.duration_minutes} {t('minutes')}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Star className="h-3 w-3 text-amber-500" />
                                  {lesson.points_reward} {t('points')}
                                </span>
                              </div>
                            </div>

                            {/* Action Button */}
                            <Link to={`/lesson/${lesson.lesson_id}`}>
                              <Button 
                                className={isCompleted ? 'bg-slate-500 hover:bg-slate-600' : 'bg-teal-500 hover:bg-teal-600'}
                                data-testid={`start-${lesson.lesson_id}`}
                              >
                                <Play className="h-4 w-4 mr-2" />
                                {isCompleted ? t('continueLesson') : t('startLesson')}
                              </Button>
                            </Link>
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              ))}

              {filteredLessons.length === 0 && (
                <div className="text-center py-12">
                  <BookOpen className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground">No lessons available for this level yet.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Mobile Bottom Nav */}
      <nav className="fixed bottom-0 left-0 right-0 bg-card border-t border-border lg:hidden">
        <div className="grid grid-cols-4 gap-1 p-2">
          <Link
            to="/dashboard"
            className="flex flex-col items-center gap-1 py-2 text-muted-foreground"
          >
            <LayoutDashboard className="h-5 w-5" />
            <span className="text-xs">{t('dashboard')}</span>
          </Link>
          <Link
            to="/lessons"
            className="flex flex-col items-center gap-1 py-2 text-primary"
          >
            <BookOpen className="h-5 w-5" />
            <span className="text-xs">{t('lessons')}</span>
          </Link>
          <Link
            to="/achievements"
            className="flex flex-col items-center gap-1 py-2 text-muted-foreground"
          >
            <Trophy className="h-5 w-5" />
            <span className="text-xs">{t('achievements')}</span>
          </Link>
          <Link
            to="/profile"
            className="flex flex-col items-center gap-1 py-2 text-muted-foreground"
          >
            <User className="h-5 w-5" />
            <span className="text-xs">{t('profile')}</span>
          </Link>
        </div>
      </nav>
    </div>
  );
}
